import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  VStack,
  Text,
} from '@chakra-ui/react';
import { ActionItem } from '../../types';

interface L10DropActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reason?: string) => Promise<void>;
  actionItem: ActionItem | null;
}

/**
 * L10 Drop Action Item Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI per Directive 5
 */
export function L10DropActionItemModal({
  isOpen,
  onClose,
  onSave,
  actionItem,
}: L10DropActionItemModalProps) {
  // Validation schema using Yup
  const validationSchema = Yup.object({
    droppedReason: Yup.string()
      .max(500, 'Reason must be at most 500 characters')
      .nullable(),
  });

  if (!actionItem) return null;

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSave(values.droppedReason || undefined);
      onClose();
    } catch (error) {
      console.error('Error dropping action item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Drop Action Item</ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={{
            droppedReason: actionItem?.droppedReason || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Text>
                    Are you sure you want to drop this action item? This action cannot be undone.
                  </Text>
                  <Text fontSize="sm" color="gray.600" fontStyle="italic">
                    {actionItem.description}
                  </Text>

                  <Field name="droppedReason">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Reason for Dropping (Optional)</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Explain why this action item is being dropped..."
                          rows={3}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" type="submit" isLoading={isSubmitting}>
                  Drop Action Item
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
